import { useCallback, useEffect, useRef } from 'react';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Scrolls the menu to the category requested by the `category` route param,
 * both when the menu mounts with one and when a mounted menu receives a new
 * one, since navigating to the focused menu screen only updates its params.
 *
 * The jump waits for the list to load instead of relying on FlashList's
 * `initialScrollIndex`. iOS clamps that early scroll back to the top, and its
 * layout pass repositions rows up to the target only, leaving the rows after
 * it drawn over the target.
 */
export const useMenuCategoryAnchor = (params: Params) => {
  const { category, scrollTo, onScrollToCategory } = params;

  // ─── Refs ────────────────────────────────────────────────────────────

  const pendingCategoryRef = useRef(category);
  const isListLoadedRef = useRef(false);

  // NOTE: `scrollTo` changes with the list rows, and a new identity must not
  //       repeat the jump.
  const scrollToRef = useRef(scrollTo);
  scrollToRef.current = scrollTo;

  const onScrollToCategoryRef = useRef(onScrollToCategory);
  onScrollToCategoryRef.current = onScrollToCategory;

  // ─── Handlers ────────────────────────────────────────────────────────

  const scrollToPendingCategory = useCallback(() => {
    if (!isListLoadedRef.current) return;

    const pendingCategory = pendingCategoryRef.current;

    if (!pendingCategory) return;

    pendingCategoryRef.current = undefined;

    onScrollToCategoryRef.current();
    scrollToRef.current(pendingCategory);
  }, []);

  const handleListLoad = useCallback(() => {
    isListLoadedRef.current = true;

    scrollToPendingCategory();
  }, [scrollToPendingCategory]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    pendingCategoryRef.current = category;

    scrollToPendingCategory();
  }, [category, scrollToPendingCategory]);

  return { handleListLoad };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  category: string | undefined;
  scrollTo: (targetId: string) => void;
  onScrollToCategory: () => void;
};
